import { trigger } from '@angular/animations';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  inject,
  InjectionToken,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { UntypedFormBuilder, Validators } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { ENVIRONMENT_LOGIN_TOKEN, ENVIRONMENT_PLATFORM_TOKEN, EnvironmentLoginInterface } from '@campus/environment';
import {
  AuthAdapterInterface,
  BaseFormComponent,
  ClassGroupInterface,
  StateEnum as State,
  StateEnum,
  StudentCodeLoginCredentials,
} from '@campus/public-portal';
import { StringFunctions } from '@campus/utils';
import { DcrAnimatedKai } from '@diekeure/dcr-components';
import '@diekeure/dcr-components/dcr-divider.js';
import '@diekeure/dcr-components/dcr-filled-button.js';
import '@diekeure/dcr-components/dcr-inline-button.js';
import '@diekeure/dcr-components/dcr-inline-icon-button.js';
import '@diekeure/dcr-components/dcr-outline-button.js';
import '@diekeure/dcr-components/dcr-select-option.js';
import '@diekeure/dcr-components/dcr-select.js';
import '@diekeure/dcr-components/dcr-text-field.js';
import '@diekeure/dcr-components/dcr-tonal-button.js';
import { Dictionary } from '@ngrx/entity';
import { Observable, timer } from 'rxjs';
import { distinctUntilChanged, filter, take, takeUntil, tap } from 'rxjs/operators';
import {
  fadeInAnimation,
  popOutAnimation,
  slideInAnimation,
  wiggleAnimation,
  wiggleLargeAnimation,
} from '../../../animations';
import { STUDENT_CODE_ADAPTER } from '../../../services/auth/student-code.adapter';
import { PersistedUserCredentialsInterface } from '../../../services/persisted-users.service.interface';

@Component({
  standalone: false,
  selector: 'kabas-login-student',
  templateUrl: './login-student.component.html',
  styleUrls: ['./login-student.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('fadeIn', fadeInAnimation),
    trigger('slideIn', slideInAnimation),
    trigger('wiggle', wiggleAnimation),
    trigger('popOut', popOutAnimation),
    trigger('wiggleLarge', wiggleLargeAnimation),
  ],
})
export class LoginStudentComponent extends BaseFormComponent<StudentCodeLoginCredentials> implements OnInit, OnChanges {
  @ViewChild('kai', { static: false }) kai: ElementRef<DcrAnimatedKai>;

  @Input() classGroups: ClassGroupInterface[];
  @Input() qrScannerVisible: boolean;
  @Input() persistedUsers: PersistedUserCredentialsInterface[];

  @Output() schoolCode = new EventEmitter<number>();
  @Output() showQrScanner = new EventEmitter<boolean>();
  @Output() ssoLoginClicked = new EventEmitter<string | InjectionToken<AuthAdapterInterface>>();

  @HostBinding('class') classList = 'kabas-login-student w-full';
  visibleCode = false;

  classGroupState: State = State.INITIAL;
  showForm = false;
  quickLoginPerson: PersistedUserCredentialsInterface;
  classGroupDict: Dictionary<ClassGroupInterface>;

  private getClassGroupsForSchool$: Observable<number>;

  // update texts based on the component's state
  public uiState = {
    loginErrors: {
      error: 'Ai, ai. Je vulde iets niet juist in.<br>Probeer eens opnieuw.', // api error
      person_with_leerid_not_found:
        'Ai, ai. Je leerID kennen we nog niet. Vraag aan je leerkracht hoe je moet aanmelden.',
    },
    success: 'We brengen je zo dadelijk naar het platform...',
    classGroups: {
      initial: ' Vul eerst je schoolcode in.',
      processing: 'Klassen aan het ophalen ...',
      success: '',
      error: 'Geen klassen gevonden voor deze school',
    },
  };
  type: string;

  private fb = inject(UntypedFormBuilder);
  private loginEnvironment: EnvironmentLoginInterface = inject(ENVIRONMENT_LOGIN_TOKEN);
  private title = inject(Title);
  private platform = inject(ENVIRONMENT_PLATFORM_TOKEN);

  showQRLoginButton = this.loginEnvironment.qr;
  showLearningIdLoginButton = this.loginEnvironment.leerId;

  ngOnChanges(changes: SimpleChanges): void {
    super.ngOnChanges(changes); // trigger manually because life cycle hooks are not triggered in base component

    if (this.state === StateEnum.SUCCESS) {
      this.kai?.nativeElement?.setState('WAVING');
    }
    if (this.isErrorState) {
      this.kai?.nativeElement?.setState('IDLE');
    }

    const { classGroups, persistedUsers } = changes;
    if (persistedUsers) {
      this.showForm = !persistedUsers.currentValue.length || this.state === 'processing' || this.state === 'success';
    }

    if (classGroups && classGroups.currentValue) {
      this.kai?.nativeElement?.setState('IDLE');
      this.classGroupDict = classGroups.currentValue.reduce(
        (acc: Dictionary<ClassGroupInterface>, cg: ClassGroupInterface) => ({ ...acc, [cg.id]: cg }),
        {}
      );
    }

    if (classGroups && !classGroups.firstChange && classGroups.currentValue !== null) {
      this.classGroupState = classGroups.currentValue.length ? State.SUCCESS : State.ERROR;
    }
  }

  ngOnInit() {
    this.title.setTitle(`${StringFunctions.capitalizeFirstLetter(this.platform)} - Leerling aanmelden`);
    this.buildForm();
    super.ngOnInit(); // trigger manually because life cycle hooks are not triggered in base component
    this.kai?.nativeElement?.setState('IDLE');

    this.getClassGroupsForSchool$ = this.form.get('schoolCode').valueChanges.pipe(
      takeUntil(this.destroy$), // complete stream
      distinctUntilChanged(),
      tap((schoolCode) => {
        this.kai?.nativeElement?.setState(!!schoolCode ? 'LOADING' : 'IDLE');
        this.classGroupState = State.INITIAL;
        if (!this.quickLoginPerson) {
          this.form.get('classGroupId').reset();
        }
        this.classGroups = [];
      }),
      filter((schoolCode) => !!schoolCode && schoolCode.toString().length >= 4)
    );

    this.getClassGroupsForSchool$.subscribe((schoolCode) => {
      this.classGroupState = State.PROCESSING;
      this.schoolCode.emit(schoolCode);
    });
  }

  trackById(index: number, classGroup: ClassGroupInterface): number {
    return classGroup.id;
  }

  quickLogin(person: PersistedUserCredentialsInterface) {
    this.form.patchValue({
      schoolCode: person.schoolCode,
      classGroupId: person.classGroupId,
      classNumber: person.classNumber,
    });
    this.quickLoginPerson = person;
    this.showForm = true;
  }

  notMeClicked() {
    this.showForm = false;
    this.quickLoginPerson = undefined;
    this.form.reset();
  }

  changedClicked() {
    this.quickLoginPerson = undefined;
  }

  public makeFormVisible() {
    this.showForm = true;
    this.showQrScanner.emit(false);
  }

  private buildForm() {
    this.form = this.fb.group({
      schoolCode: ['', { validators: [Validators.required, Validators.min(1000)] }],
      classGroupId: [null, { validators: [Validators.required], updateOn: 'blur' }],
      classNumber: ['', Validators.required],
      childCode: ['', Validators.required],
    });
  }

  public submit() {
    this.kai?.nativeElement?.setState('LOADING');
    timer(400)
      .pipe(take(1))
      .subscribe(() => {
        super.submit(STUDENT_CODE_ADAPTER);
      });
  }

  public ssoLogin(adapter: string | InjectionToken<AuthAdapterInterface>) {
    this.ssoLoginClicked.emit(adapter);
  }
}
